/**
 * Pricing Rule Card Component
 * 
 * Displays a lead type with its base price and state overrides.
 * Admin can edit prices inline and add new state overrides.
 */

import { useState } from "react";
import { PricingRule } from "@/contexts/PricingRulesContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronDown, ChevronUp, X } from "lucide-react";

// US States for override selection
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

interface PricingRuleCardProps {
  leadTypeId: string;
  leadTypeName: string;
  vertical: "life_insurance" | "mortgage";
  baseRule: PricingRule | null;
  stateOverrides: PricingRule[];
  onUpdatePrice: (id: string, newPrice: number) => void;
  onToggleActive: (id: string) => void;
  onAddOverride: (rule: Omit<PricingRule, "id">) => void;
  onDeleteRule: (id: string) => void;
}

export function PricingRuleCard({
  leadTypeId,
  leadTypeName,
  vertical,
  baseRule,
  stateOverrides,
  onUpdatePrice,
  onToggleActive,
  onAddOverride,
  onDeleteRule,
}: PricingRuleCardProps) {
  const [isExpanded, setIsExpanded] = useState(stateOverrides.length > 0);
  const [isAddingOverride, setIsAddingOverride] = useState(false);
  const [newOverrideState, setNewOverrideState] = useState("");
  const [newOverridePrice, setNewOverridePrice] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  // Get states already used
  const usedStates = stateOverrides.map((r) => r.state);
  const availableStates = US_STATES.filter((s) => !usedStates.includes(s));

  const formatVertical = (v: string) => 
    v === "life_insurance" ? "Life Insurance" : "Mortgage";

  const handleStartEdit = (rule: PricingRule) => {
    setEditingId(rule.id);
    setEditValue(rule.basePrice.toString());
  };

  const handleSaveEdit = (id: string) => {
    const newPrice = parseFloat(editValue);
    if (!isNaN(newPrice) && newPrice >= 0) {
      onUpdatePrice(id, newPrice);
    }
    setEditingId(null);
    setEditValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === "Enter") handleSaveEdit(id);
    else if (e.key === "Escape") {
      setEditingId(null);
      setEditValue("");
    }
  };

  const handleAddOverride = () => {
    const price = parseFloat(newOverridePrice);
    if (newOverrideState && !isNaN(price) && price >= 0) {
      onAddOverride({
        vertical,
        leadTypeId,
        leadTypeName,
        state: newOverrideState,
        basePrice: price,
        active: true,
      });
      setNewOverrideState("");
      setNewOverridePrice("");
      setIsAddingOverride(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">{leadTypeName}</CardTitle>
            <Badge variant="outline" className="font-normal text-xs">
              {formatVertical(vertical)}
            </Badge>
          </div>
          {stateOverrides.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-muted-foreground"
            >
              {stateOverrides.length} override{stateOverrides.length !== 1 ? "s" : ""}
              {isExpanded ? (
                <ChevronUp className="ml-1 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-1 h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Base Price (ALL States) */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <span className="text-sm font-medium">Default (All States)</span>
              <span className="text-xs text-muted-foreground">
                Applied when no state override exists
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {baseRule ? (
              <>
                {editingId === baseRule.id ? (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">$</span>
                    <Input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleSaveEdit(baseRule.id)}
                      onKeyDown={(e) => handleKeyDown(e, baseRule.id)}
                      className="w-20 h-8"
                      autoFocus
                      min={0}
                      step={1}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => handleStartEdit(baseRule)}
                    className="text-xl font-bold font-mono hover:bg-muted px-2 py-1 rounded transition-colors"
                    disabled={!baseRule.active}
                  >
                    ${baseRule.basePrice.toFixed(2)}
                  </button>
                )}
                <Switch
                  checked={baseRule.active}
                  onCheckedChange={() => onToggleActive(baseRule.id)}
                />
              </>
            ) : (
              <span className="text-muted-foreground text-sm">No base price set</span>
            )}
          </div>
        </div>

        {/* State Overrides Section */}
        {(isExpanded || stateOverrides.length === 0) && (
          <div className="space-y-2">
            {stateOverrides.length > 0 && (
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide pl-1">
                State Overrides
              </div>
            )}
            
            {stateOverrides.map((override) => (
              <div
                key={override.id}
                className={`flex items-center justify-between p-3 border rounded-lg ml-4 ${
                  !override.active ? "opacity-50" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="font-mono">
                    {override.state}
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
                    Overrides Default
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  {editingId === override.id ? (
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => handleSaveEdit(override.id)}
                        onKeyDown={(e) => handleKeyDown(e, override.id)}
                        className="w-20 h-8"
                        autoFocus
                        min={0}
                        step={1}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => handleStartEdit(override)}
                      className="font-bold font-mono hover:bg-muted px-2 py-1 rounded transition-colors"
                      disabled={!override.active}
                    >
                      ${override.basePrice.toFixed(2)}
                    </button>
                  )}
                  <Switch
                    checked={override.active}
                    onCheckedChange={() => onToggleActive(override.id)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => onDeleteRule(override.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Add Override Form */}
            {isAddingOverride ? (
              <div className="flex items-center gap-3 p-3 border border-dashed rounded-lg ml-4">
                <Select value={newOverrideState} onValueChange={setNewOverrideState}>
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStates.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    type="number"
                    placeholder="Price"
                    value={newOverridePrice}
                    onChange={(e) => setNewOverridePrice(e.target.value)}
                    className="w-24"
                    min={0}
                    step={1}
                  />
                </div>
                <Button size="sm" onClick={handleAddOverride}>
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingOverride(false);
                    setNewOverrideState("");
                    setNewOverridePrice("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="ml-4"
                onClick={() => setIsAddingOverride(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add State Override
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
